import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Languages, Save, RefreshCw } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';

const FIELDS = [
  { key: 'tone_style', label: 'Tone Style', options: ['professional', 'natural_professional', 'natural_sports', 'dynamic_sports', 'analytical', 'clean_professional', 'natural_football', 'clear_data_driven', 'strong_analytical', 'trustworthy_clear', 'formal_professional', 'easy_natural'] },
  { key: 'professionalism_level', label: 'Professionalism', options: ['low', 'medium', 'medium_high', 'high'] },
  { key: 'friendliness_level', label: 'Friendliness', options: ['low', 'low_medium', 'medium_low', 'medium', 'medium_high', 'high'] },
  { key: 'sentence_length_preference', label: 'Sentence Length', options: ['short', 'short_medium', 'medium', 'medium_long', 'long'] },
  { key: 'seo_intensity', label: 'SEO Intensity', options: ['low', 'medium', 'high'] },
];

const BOOL_FIELDS = [
  { key: 'include_cta', label: 'Include CTA' },
  { key: 'betting_safety_mode', label: 'Betting Safety Mode' },
];

export default function AdminLanguageSettings() {
  const at = useAdminT();
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/seo/language-settings`, { headers });
      const data = await res.json();
      setSettings(data.settings || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (s) => {
    setEditing(s.language);
    setForm({ ...s });
  };

  const handleSave = async (lang) => {
    setSaving(true);
    await fetch(`${API_BASE}/api/admin/seo/language-settings/${lang}`, {
      method: 'PUT', headers, body: JSON.stringify(form),
    });
    setSaving(false);
    setEditing(null);
    fetchData();
  };

  if (loading) return <div className="p-6 text-slate-400">{at.loading}</div>;

  return (
    <div className="space-y-6" data-testid="admin-language-settings">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <Languages className="w-6 h-6 inline mr-2 text-cyan-400" /> {at.languageToneSettings}
        </h1>
        <Button size="sm" onClick={fetchData} className="bg-slate-800 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-1" /> {at.refresh}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {settings.map(s => {
          const isEditing = editing === s.language;
          return (
            <div key={s.language} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={`lang-setting-${s.language}`}>
              <div className="flex items-center justify-between mb-3">
                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-sm font-bold">{s.language?.toUpperCase()}</Badge>
                {!isEditing ? (
                  <Button size="sm" variant="ghost" onClick={() => startEdit(s)} className="text-slate-400 hover:text-blue-400 h-7 px-2">Edit</Button>
                ) : (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleSave(s.language)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 px-2">
                      <Save className="w-3 h-3 mr-1" />{saving ? '...' : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-slate-400 h-7 px-2">Cancel</Button>
                  </div>
                )}
              </div>

              {!isEditing ? (
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Tone</span><span className="text-slate-300">{s.tone_style}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Professionalism</span><span className="text-slate-300">{s.professionalism_level}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Friendliness</span><span className="text-slate-300">{s.friendliness_level}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Sentence Length</span><span className="text-slate-300">{s.sentence_length_preference}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">SEO</span><span className="text-slate-300">{s.seo_intensity}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">CTA</span><span className={s.include_cta ? 'text-emerald-400' : 'text-slate-500'}>{s.include_cta ? 'Yes' : 'No'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Safety</span><span className={s.betting_safety_mode ? 'text-emerald-400' : 'text-red-400'}>{s.betting_safety_mode ? 'On' : 'Off'}</span></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {FIELDS.map(f => (
                    <div key={f.key} className="flex items-center justify-between gap-2">
                      <label className="text-xs text-slate-500 shrink-0">{f.label}</label>
                      <select value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                        className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 w-40">
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                  {BOOL_FIELDS.map(f => (
                    <div key={f.key} className="flex items-center justify-between gap-2">
                      <label className="text-xs text-slate-500">{f.label}</label>
                      <input type="checkbox" checked={form[f.key] || false}
                        onChange={e => setForm({ ...form, [f.key]: e.target.checked })}
                        className="rounded border-slate-700" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
